import { authLinkPaths, loginInput, mfaMethods, resetPasswordInput } from "@nexxt/contracts/identity";
import * as z from "zod";

// The roads into an app of the external users (ADR-147, ADR-151): where a sign-in goes back to, the second step it may
// ask for, and the token of the link of an email. URLs anyone can edit: a malformed value falls back instead of
// failing.

/**
 * Where an app of the external users mounts the pages of this package (ADR-207): the screens link to each other by
 * these paths, and identity's emails lead to the reset, the invitation and the new email (`authLinkPaths` of the
 * contract, ADR-147, ADR-163, ADR-216).
 */
export const accountPaths = {
  signIn: "/sign-in",
  secondStep: "/sign-in/verify",
  forgotPassword: "/forgot-password",
  resetPassword: authLinkPaths.passwordReset,
  acceptInvitation: authLinkPaths.invitation,
  confirmEmail: authLinkPaths.emailChange,
  account: "/account",
  security: "/security",
} as const;

/** A path of this app, never another site: the rules of `loginInput` (no `//`, no backslash, no control character). */
const returnTo = loginInput.shape.returnTo.catch("/");

export const signInSearch = z.object({ returnTo });

/** The second step: the methods the password answer offered, which only steer the page; the gateway decides. */
export const secondStepSearch = z.object({ methods: z.array(z.enum(mfaMethods)).catch([]), returnTo });

export const signInHref = (to: string) => `${accountPaths.signIn}?returnTo=${encodeURIComponent(to)}`;

/** Where a session that opened goes: to adding a second factor first, when a role requires one (ADR-151). */
export const landingOf = (signedIn: { mfaEnrollmentRequired: boolean }, to: string): string =>
  signedIn.mfaEnrollmentRequired ? accountPaths.security : to;

const token = resetPasswordInput.shape.token;

/**
 * The token of an invitation or a reset: only in the fragment of the link (`#token=…`), so that no server, log or
 * `Referer` ever sees it (ADR-147, point 6). Null when the link is not one of ours.
 */
export function linkToken(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  return token.safeParse(value).success ? value : null;
}
